import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Nav } from 'react-bootstrap';
import momentTimezone from 'moment-timezone';
import Plotly from 'plotly.js';
import createPlotlyComponent from 'react-plotly.js/factory';
import DatePicker from '../datepicker';
import SensorService from '../../services/sensorService';
import { AlertSensorValues } from '../LogDataModal';
import '../maintenance-checklist/maintenance-checklist.css';
import '../AlertLogsModal/alertLogsModal.css';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  ReferenceLine,
} from 'recharts';

const Plot = createPlotlyComponent(Plotly);

export interface Props {
  show: boolean;
  onClose: () => void;
  alertLog: any;
  sensorBoxName?: string;
  loading?: boolean;
}

export const healthChartToAlertLog = (chart: any, titleName?: string, request?: any) => {
  const series = Array.isArray(chart?.series) ? chart.series : [];
  const sharedUnit = typeof chart?.unit === 'string' ? chart.unit.trim() : '';
  const seriesLabels: Record<string, string> = {};
  const sourceFields: string[] = [];
  series.forEach((item: any) => {
    const key = String(item?.key || '');
    if (!key) return;
    sourceFields.push(key);
    const name = String(item?.label || key);
    seriesLabels[key] = !sharedUnit && item?.unit ? `${name} (${item.unit})` : name;
  });
  const chartSeries = sourceFields.map((key) => {
    const item = series.find((entry: any) => String(entry?.key || '') === key);
    return {
      key,
      label: seriesLabels[key] || key,
      unit: String(item?.unit || '').trim(),
    };
  });
  const code = String(chart?.code || '').replace(/#.*$/, '');
  const head = titleName && code
    ? `${titleName} - ${code}`
    : (code || titleName || 'Health Alert');
  return {
    chartTitle: head,
    healthChartRequest: request || null,
    trigger_code: {
      code,
      description: chart?.description || '',
    },
    ml: {
      chart: {
        points: Array.isArray(chart?.points) ? chart.points : [],
        unit: sharedUnit,
        y_label: sharedUnit ? `Value (${sharedUnit})` : 'Value',
        marker_ms: chart?.timestamp,
        series_labels: seriesLabels,
        series: chartSeries,
        source_fields: sourceFields,
      },
    },
  };
};

const PHASE_SUFFIX = /_(1phase|3phase)$/i;
const SERIES_COLORS = ['#1a3a6e', '#d32f2f', '#2e7d32', '#ed6c02', '#6a1b9a'];

const pickLabel = (...candidates: Array<any>): string => {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

export const toEpochMs = (value: any): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'object') {
    return toEpochMs(value.$numberLong ?? value.$date ?? value.t ?? value.timestamp);
  }
  return null;
};

const toFiniteNumber = (value: any): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const titleCaseWords = (raw: string): string =>
  raw
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatComponentName = (comp: any): string => {
  const compType = pickLabel(comp?.comp_type);
  if (!compType) return '';
  const pretty = titleCaseWords(compType.replace(PHASE_SUFFIX, ''));
  if (comp?.seq == null || String(comp.seq).trim() === '') return pretty;
  return `${pretty} - ${comp.seq}`;
};

const parseComponentFromPrereq = (prereq: any): { comp_type: string; seq?: string } | null => {
  if (typeof prereq !== 'string' || !prereq.trim()) return null;
  const left = prereq.split('.')[0]?.trim();
  if (!left) return null;
  const match = left.match(/^(.*)_(\d+)$/);
  if (match) return { comp_type: match[1], seq: match[2] };
  return { comp_type: left };
};

const getPrereqs = (alertLog: any): string[] => {
  const prereqs =
    alertLog?.trigger_code?.trigger_pre_req ||
    alertLog?.trigger_codes?.[0]?.trigger_pre_req ||
    [];
  return Array.isArray(prereqs) ? prereqs : [prereqs];
};

const pickSensorbox = (alertLog: any): { name: string; id: string } => {
  const firstComp = Array.isArray(alertLog?.components) ? alertLog.components[0] : null;
  const name = pickLabel(
    firstComp?.sensorbox_name,
    alertLog?.sensorbox_name,
    alertLog?.ml?.sensorbox_name
  );
  const id = pickLabel(
    firstComp?.sensorbox_id,
    alertLog?.sensorbox_id,
    alertLog?.ml?.sensorbox_id
  );
  return { name, id };
};

const isIndividualSensorboxAlert = (alertLog: any): boolean => {
  const assetId = pickLabel(alertLog?.asset_id);
  return /^SYS/i.test(assetId);
};

const buildTitle = (alertLog: any, sensorBoxName?: string): string => {
  const explicit = pickLabel(alertLog?.chartTitle);
  if (explicit) return explicit;

  const assetName = pickLabel(alertLog?.asset_name, sensorBoxName);
  const firstComp = Array.isArray(alertLog?.components) ? alertLog.components[0] : null;
  let componentName = formatComponentName(firstComp);
  if (!componentName) {
    componentName = formatComponentName(parseComponentFromPrereq(getPrereqs(alertLog)[0]));
  }

  const { name: sensorboxName, id: sensorboxId } = pickSensorbox(alertLog);
  const sensorboxLabel = pickLabel(sensorboxName, sensorboxId);
  const sensorboxPart =
    sensorboxLabel && !isIndividualSensorboxAlert(alertLog) ? `(${sensorboxLabel})` : '';

  const head =
    assetName && componentName
      ? `${assetName} - ${componentName}`
      : pickLabel(assetName, componentName, 'Prediction Alert');

  return sensorboxPart ? `${head} ${sensorboxPart}` : head;
};

export const formatMlAlertLocalTime = (ms: number, pattern: string) =>
  momentTimezone(ms).format(pattern);

export const getMlAlertTimezoneLabel = (ms?: number): string => {
  const zone = momentTimezone.tz.guess();
  const instant =
    ms != null ? momentTimezone.tz(ms, zone) : momentTimezone.tz(zone);
  return (instant.format('z') || zone || '').toUpperCase();
};

const formatAxisTime = (ms: number) => formatMlAlertLocalTime(ms, 'MM/DD HH:mm');
const formatTooltipTime = (ms: number) => formatMlAlertLocalTime(ms, 'MM/DD/YYYY, HH:mm:ss');

const metricFromPrereq = (alertLog: any): string => {
  const first = getPrereqs(alertLog)[0];
  if (typeof first !== 'string') return '';
  const metric = first.split('.').pop()?.trim() || '';
  return metric ? titleCaseWords(metric) : '';
};

const buildYLabel = (chart: any, alertLog: any): string => {
  const fromChart = pickLabel(
    chart.y_label,
    chart.ylabel,
    chart.y_axis_label,
    chart.metric_label,
    chart.metric
  );
  if (fromChart) return fromChart;

  const unit = pickLabel(chart.unit);
  const metric = metricFromPrereq(alertLog);
  if (metric && unit) return `${metric} (${unit})`;
  if (metric) return metric;
  if (unit) return `Value (${unit})`;
  return 'Value';
};

const formatSeriesName = (field: string): string => {
  const match = field.match(/^(.*)_l(\d+)$/i);
  if (match) return `L${match[2]} ${titleCaseWords(match[1])}`;
  return titleCaseWords(field);
};

const resolveSeriesKeys = (chart: any, points: any[]): string[] => {
  const seen = new Set<string>();
  const fromPoints: string[] = [];
  points.forEach((point) => {
    const values = point && typeof point === 'object' ? point.values : null;
    if (!values || typeof values !== 'object') return;
    Object.keys(values).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        fromPoints.push(key);
      }
    });
  });
  const declared = Array.isArray(chart?.source_fields)
    ? chart.source_fields.filter((key: any) => typeof key === 'string' && key.trim() && seen.has(key))
    : [];
  if (declared.length) {
    const declaredSet = new Set(declared);
    return [...declared, ...fromPoints.filter((key) => !declaredSet.has(key))];
  }
  if (fromPoints.length) return fromPoints;
  if (Array.isArray(chart?.source_fields) && chart.source_fields.length) {
    return chart.source_fields.filter((key: any) => typeof key === 'string' && key.trim());
  }
  return [];
};

type ChartPoint = { time: number } & Record<string, number>;

const normalizeChartSeries = (
  chart: any,
  alertLog: any
): { data: ChartPoint[]; seriesKeys: string[] } => {
  const points = chart?.points;
  if (!Array.isArray(points)) return { data: [], seriesKeys: [] };

  const peakTimestamps = alertLog?.ml?.peak_window?.timestamps;
  const seriesKeys = resolveSeriesKeys(chart, points);
  const useSeries = seriesKeys.length > 0;

  const parsed = points
    .map((point: any, index: number) => {
      let time: number | null = null;
      const row: Record<string, number> = {};

      if (point && typeof point === 'object') {
        time = toEpochMs(point.t ?? point.timestamp ?? point.time ?? point.x);
        if (useSeries) {
          seriesKeys.forEach((key) => {
            const value = toFiniteNumber(point.values?.[key]);
            if (value != null) row[key] = value;
          });
        } else {
          const value = toFiniteNumber(point.y ?? point.value ?? point.v);
          if (value != null) row.value = value;
        }
      } else {
        const value = toFiniteNumber(point);
        if (value != null) row.value = value;
      }

      if (time == null && Array.isArray(peakTimestamps)) {
        time = toEpochMs(peakTimestamps[index]);
      }

      const hasValue = Object.keys(row).length > 0;
      if (!hasValue) return null;
      return { time, row, index };
    })
    .filter(Boolean) as Array<{ time: number | null; row: Record<string, number>; index: number }>;

  const hasTimestamps = parsed.some((p) => p.time != null);
  const data = parsed.map((p) => ({
    time: hasTimestamps ? (p.time ?? p.index) : p.index,
    ...p.row,
  }));

  return { data, seriesKeys: useSeries ? seriesKeys : data.length ? ['value'] : [] };
};

const looksLikeEpochMs = (value: number) => Number.isFinite(value) && value > 1e11;

const PLOT_COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
const COPY_PLOT_ICON = {
  width: 524,
  height: 524,
  path: 'M408 112H184c-13.2 0-24 10.8-24 24v32h48v-16h176v176h-16v48h32c13.2 0 24-10.8 24-24V136c0-13.2-10.8-24-24-24zM328 192H104c-13.2 0-24 10.8-24 24v208c0 13.2 10.8 24 24 24h224c13.2 0 24-10.8 24-24V216c0-13.2-10.8-24-24-24zM320 416H112V224h208v192z',
};

const axisTitle = (unit: string) => unit || 'Value';

const HealthAlertPlot: React.FC<{
  data: ChartPoint[];
  seriesKeys: string[];
  chart: any;
  xLabel: string;
  markerMs: number | null;
}> = ({ data, seriesKeys, chart, xLabel, markerMs }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const seriesMeta = Array.isArray(chart?.series) ? chart.series : [];
  const units = useMemo(() => {
    const found = seriesMeta
      .map((item: any) => String(item?.unit || '').trim())
      .filter(Boolean);
    return [...new Set(found)] as string[];
  }, [seriesMeta]);
  const dualAxis = units.length >= 2;
  const rightUnit = dualAxis ? units[1] : '';

  const traces = useMemo(() => seriesKeys.map((key, index) => {
    const meta = seriesMeta.find((item: any) => item?.key === key);
    const xs: Date[] = [];
    const ys: number[] = [];
    data.forEach((point) => {
      const value = point[key];
      if (typeof value !== 'number' || !Number.isFinite(value)) return;
      if (!looksLikeEpochMs(point.time)) return;
      xs.push(new Date(point.time));
      ys.push(value);
    });
    const unit = String(meta?.unit || '').trim();
    return {
      x: xs,
      y: ys,
      type: 'scatter',
      mode: 'lines',
      name: String(meta?.label || key),
      showlegend: true,
      connectgaps: true,
      yaxis: dualAxis && unit === rightUnit ? 'y2' : 'y',
      line: { color: PLOT_COLORS[index % PLOT_COLORS.length], width: 2 },
    };
  }).filter((trace) => trace.x.length), [data, seriesKeys, seriesMeta, dualAxis, rightUnit]);

  const layout = useMemo(() => {
    const markerDate = markerMs != null ? new Date(markerMs) : null;
    const revision = data.length ? `${data[0].time}-${data[data.length - 1].time}` : 'health-alert';
    const next: any = {
      autosize: true,
      height: 460,
      margin: { t: 100, r: dualAxis ? 64 : 24, b: 72, l: 64 },
      uirevision: revision,
      legend: {
        orientation: 'h',
        x: 0,
        y: 1.12,
        xanchor: 'left',
        yanchor: 'bottom',
      },
      showlegend: true,
      xaxis: {
        type: 'date',
        title: xLabel,
        tickformat: '%I:%M:%S %p\n%B %d, %Y',
        autorange: true,
        uirevision: revision,
      },
      yaxis: {
        title: axisTitle(units[0] || String(chart?.unit || '')),
        titlefont: { color: 'black' },
        tickfont: { color: 'black' },
      },
    };
    if (dualAxis) {
      next.yaxis2 = {
        title: axisTitle(rightUnit),
        overlaying: 'y',
        side: 'right',
        titlefont: { color: 'black' },
        tickfont: { color: 'black' },
      };
    }
    if (markerDate) {
      next.shapes = [{
        type: 'line',
        x0: markerDate,
        x1: markerDate,
        y0: 0,
        y1: 1,
        yref: 'paper',
        line: { color: '#b71c1c', width: 1, dash: 'dot' },
      }];
      next.annotations = [{
        x: markerDate,
        y: 1,
        yref: 'paper',
        text: 'Alert',
        showarrow: false,
        yanchor: 'bottom',
        font: { color: '#b71c1c', size: 11 },
      }];
    }
    return next;
  }, [chart, data, dualAxis, markerMs, rightUnit, units, xLabel]);

  const handleCopy = useCallback(async () => {
    try {
      const plotElement = plotRef.current?.querySelector('.js-plotly-plot');
      if (!plotElement) return;
      const imageData = await Plotly.toImage(plotElement as any, {
        format: 'png',
        height: 400,
        width: 1200,
        scale: 3,
      });
      const response = await fetch(imageData);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const plotElement = plotRef.current?.querySelector('.js-plotly-plot');
      if (plotElement) Plotly.Plots.resize(plotElement as any);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [traces]);

  const config = useMemo(() => ({
    scrollZoom: false,
    displaylogo: false,
    displayModeBar: true,
    responsive: true,
    toImageButtonOptions: {
      format: 'svg' as const,
      filename: 'alert_chart',
      height: 500,
      width: 700,
      scale: 1,
    },
    modeBarButtonsToAdd: [{
      title: 'Copy plot',
      name: 'Copy plot',
      icon: COPY_PLOT_ICON,
      click: handleCopy,
    }],
  }), [handleCopy]);

  return (
    <div ref={plotRef} style={{ width: '100%', height: 460 }}>
      <Plot
        data={traces as any}
        layout={layout}
        config={config as any}
        useResizeHandler
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

const HALF_WINDOW_MS = 12 * 60 * 60 * 1000;
const MAX_RANGE_MS = 31 * 24 * 60 * 60 * 1000;

const startOfLocalDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfLocalDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const MlAlertChartModal: React.FC<Props> = ({ show, onClose, alertLog, sensorBoxName, loading = false }) => {
  const request = alertLog?.healthChartRequest;
  const alertMs = toEpochMs(alertLog?.ml?.chart?.marker_ms);
  const requestKey = request
    ? `${request.logId}|${request.code}|${request.sensorbox || ''}|${request.description || ''}`
    : '';
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [rangeChart, setRangeChart] = useState<any>(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState('');
  const [healthTab, setHealthTab] = useState<'graph' | 'data'>('graph');

  useEffect(() => {
    setRangeChart(null);
    setRangeError('');
    setRangeLoading(false);
    setHealthTab('graph');
    if (alertMs == null) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    setStartDate(startOfLocalDay(new Date(alertMs - HALF_WINDOW_MS)));
    setEndDate(startOfLocalDay(new Date(alertMs + HALF_WINDOW_MS)));
  }, [requestKey, alertMs]);

  const chart = rangeChart || alertLog?.ml?.chart || {};
  const { data, seriesKeys } = useMemo(
    () => normalizeChartSeries(chart, alertLog),
    [chart, alertLog]
  );
  const usesTimestamps = data.some((point) => looksLikeEpochMs(point.time));
  const showLegend = seriesKeys.length > 1 || (seriesKeys.length === 1 && seriesKeys[0] !== 'value');
  const seriesLabels = chart.series_labels && typeof chart.series_labels === 'object' ? chart.series_labels : {};
  const legendName = (key: string) => {
    const custom = seriesLabels[key];
    if (typeof custom === 'string' && custom.trim()) return custom.trim();
    return formatSeriesName(key);
  };
  const markerMs = toEpochMs(chart.marker_ms);

  const title = buildTitle(alertLog, sensorBoxName);
  const description = pickLabel(alertLog?.trigger_code?.description);
  const yLabel = buildYLabel(chart, alertLog);
  const timezoneLabel = usesTimestamps ? getMlAlertTimezoneLabel(data[0]?.time) : '';
  const baseXLabel = pickLabel(
    chart.x_label,
    chart.xlabel,
    chart.x_axis_label,
    'Occurrence time'
  );
  const xLabel = timezoneLabel ? `${baseXLabel} (${timezoneLabel})` : baseXLabel;
  const isHealthChart = Boolean(alertLog?.chartTitle);
  const chartBusy = loading || rangeLoading;

  const loadRange = async (start: Date, end: Date) => {
    if (!request?.logId || !request?.code) return;
    const rangeStart = startOfLocalDay(start);
    const rangeEnd = endOfLocalDay(end);
    if (rangeEnd.getTime() <= rangeStart.getTime()) {
      setRangeError('Start date must be before end date');
      return;
    }
    if (rangeEnd.getTime() - rangeStart.getTime() > MAX_RANGE_MS) {
      setRangeError('Date range cannot exceed 31 days');
      return;
    }
    setRangeError('');
    setRangeLoading(true);
    try {
      const next = await SensorService.getHealthAlertChart(
        request.logId,
        request.code,
        request.sensorbox,
        request.description,
        rangeStart.getTime(),
        rangeEnd.getTime()
      );
      const mapped = healthChartToAlertLog(next, alertLog?.chartTitle, request);
      setRangeChart(mapped.ml.chart);
    } catch (error) {
      setRangeError('Could not load this time range');
    } finally {
      setRangeLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered className="logs-modal-body">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          <div>{title}</div>
          {description && description !== title && (
            <div style={{ fontSize: '0.85rem', fontWeight: 400, color: '#555', marginTop: 4 }}>
              {description}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isHealthChart && (
          <Nav
            variant="pills"
            className="checklist-tab health-chart-tabs mb-3"
            activeKey={healthTab}
            onSelect={(key) => {
              if (key === 'graph' || key === 'data') setHealthTab(key);
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey="graph">Graph</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="data">Data</Nav.Link>
            </Nav.Item>
          </Nav>
        )}
        {isHealthChart && healthTab === 'data' ? (
          <AlertSensorValues
            logData={alertLog?.sensorValues?.values}
            highlightKeys={alertLog?.sensorValues?.highlightKeys}
            alertColor={alertLog?.sensorValues?.alertColor}
            showToolbar={false}
          />
        ) : (
        <>
        {isHealthChart && healthTab === 'graph' && startDate && endDate && (
          <div className="row mb-3">
            <div className="col-md-5">
              <label className="mb-1">Start Date</label>
              <DatePicker
                value={startDate}
                maxDate={endDate}
                placeholderText="Start Date"
                disabled={chartBusy}
                onChange={(value: Date) => {
                  if (!value || !endDate) return;
                  setStartDate(value);
                  loadRange(value, endDate);
                }}
              />
            </div>
            <div className="col-md-5">
              <label className="mb-1">End Date</label>
              <DatePicker
                value={endDate}
                minDate={startDate}
                placeholderText="End Date"
                disabled={chartBusy}
                onChange={(value: Date) => {
                  if (!value || !startDate) return;
                  setEndDate(value);
                  loadRange(startDate, value);
                }}
              />
            </div>
            {rangeError && (
              <div className="col-12">
                <p className="text-danger mb-0 mt-2">{rangeError}</p>
              </div>
            )}
          </div>
        )}
        {chartBusy ? (
          <p className="no-logs-text mb-0">Loading chart...</p>
        ) : data.length > 0 && isHealthChart ? (
          <HealthAlertPlot
            data={data}
            seriesKeys={seriesKeys}
            chart={chart}
            xLabel={xLabel}
            markerMs={usesTimestamps ? markerMs : null}
          />
        ) : data.length > 0 ? (
          <div style={{ width: '100%', height: showLegend ? 440 : 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 16, right: 28, bottom: showLegend ? 88 : 44, left: 20 }}
              >
                <CartesianGrid stroke="#d0d0d0" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={
                    usesTimestamps && markerMs != null
                      ? [
                          (dataMin: number) => Math.min(dataMin, markerMs),
                          (dataMax: number) => Math.max(dataMax, markerMs),
                        ]
                      : ['dataMin', 'dataMax']
                  }
                  tickFormatter={(value) =>
                    usesTimestamps ? formatAxisTime(Number(value)) : String(value)
                  }
                  tick={{ fontSize: 11 }}
                  minTickGap={48}
                  interval="preserveStartEnd"
                >
                  <Label value={xLabel} position="insideBottom" offset={-26} style={{ fontSize: 13 }} />
                </XAxis>
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} width={72}>
                  <Label
                    value={yLabel}
                    angle={-90}
                    position="insideLeft"
                    offset={10}
                    style={{ fontSize: 13, textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip
                  formatter={(value: any, name: any) => [
                    Number(value).toFixed(2),
                    name === 'value' ? yLabel : legendName(String(name)),
                  ]}
                  labelFormatter={(label) => {
                    if (!usesTimestamps) return `Data ${label}`;
                    const formatted = formatTooltipTime(Number(label));
                    return timezoneLabel ? `${formatted} ${timezoneLabel}` : formatted;
                  }}
                />
                {showLegend && (
                  <Legend
                    verticalAlign="bottom"
                    align="left"
                    iconType="line"
                    wrapperStyle={{ paddingTop: 36 }}
                    formatter={(value) => legendName(String(value))}
                  />
                )}
                {seriesKeys.map((key, index) => (
                  <Line
                    key={key}
                    type="linear"
                    dataKey={key}
                    name={key}
                    stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
                {usesTimestamps && markerMs != null && (
                  <ReferenceLine
                    x={markerMs}
                    stroke="#b71c1c"
                    strokeDasharray="4 4"
                    ifOverflow="extendDomain"
                    label={{ value: 'Alert', position: 'insideTopRight', fontSize: 11, fill: '#b71c1c' }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="no-logs-text mb-0">No chart data available</p>
        )}
        </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MlAlertChartModal;